(() => {
  'use strict';

  if (window.AuraraSmoothScroll) {
    window.AuraraSmoothScroll.refresh();
    return;
  }

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class AuraraSmoothScrollController {
    constructor(options) {
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.current = window.scrollY || window.pageYOffset || 0;
      this.target = this.current;
      this.frame = null;
      this.isAnimating = false;
      this.lerp = .09;
      this.enabled = false;
      this.onWheel = this.handleWheel.bind(this);
      this.onScroll = this.handleNativeScroll.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onMotionChange = this.refreshAvailability.bind(this);

      window.addEventListener('wheel', this.onWheel, { passive: false });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('keydown', this.onKeyDown, { passive: true });
      this.motionQuery.addEventListener('change', this.onMotionChange);
      this.update(options);
    }

    update(options = {}) {
      this.enabled = options.enabled === true;
      const requestedLerp = Number(options.lerp);
      this.lerp = Number.isFinite(requestedLerp) ? clamp(requestedLerp, .05, .2) : .09;
      this.refreshAvailability();
    }

    get canSmooth() {
      // The source Layout only opts out for reduced-motion. Touch scrolling
      // remains native because this controller handles wheel events only.
      return this.enabled && !this.motionQuery.matches;
    }

    get maximumScroll() {
      const documentElement = document.documentElement;
      const body = document.body;
      return Math.max(0, Math.max(documentElement.scrollHeight, body ? body.scrollHeight : 0) - window.innerHeight);
    }

    handleWheel(event) {
      if (!this.canSmooth || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || !event.deltaY) return;
      if (this.hasScrollableAncestor(event.target, event.deltaY)) return;

      event.preventDefault();
      const multiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      this.target = clamp(this.target + event.deltaY * multiplier, 0, this.maximumScroll);
      this.start();
    }

    hasScrollableAncestor(target, deltaY) {
      let element = target instanceof Element ? target : null;
      while (element && element !== document.body) {
        if (element.matches('[data-aurara-disable-smooth-scroll], input, textarea, select, [contenteditable="true"]')) return true;
        const styles = window.getComputedStyle(element);
        const scrollable = /(auto|scroll|overlay)/.test(styles.overflowY) && element.scrollHeight > element.clientHeight + 1;
        if (scrollable) {
          if ((deltaY < 0 && element.scrollTop > 0) || (deltaY > 0 && element.scrollTop + element.clientHeight < element.scrollHeight - 1)) return true;
        }
        element = element.parentElement;
      }
      return false;
    }

    handleNativeScroll() {
      if (this.isAnimating) return;
      this.current = window.scrollY || window.pageYOffset || 0;
      this.target = this.current;
    }

    handleKeyDown(event) {
      if (!this.isAnimating) return;
      if ([' ', 'PageDown', 'PageUp', 'Home', 'End', 'ArrowDown', 'ArrowUp'].includes(event.key)) this.stop();
    }

    start() {
      if (this.frame) return;
      this.isAnimating = true;
      this.frame = window.requestAnimationFrame(() => this.tick());
    }

    tick() {
      this.frame = null;
      if (!this.canSmooth) {
        this.stop();
        return;
      }

      this.target = clamp(this.target, 0, this.maximumScroll);
      const difference = this.target - this.current;
      if (Math.abs(difference) < .35) {
        this.current = this.target;
        window.scrollTo(0, this.current);
        this.isAnimating = false;
        return;
      }

      this.current += difference * this.lerp;
      window.scrollTo(0, this.current);
      this.frame = window.requestAnimationFrame(() => this.tick());
    }

    stop() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = null;
      this.current = window.scrollY || window.pageYOffset || 0;
      this.target = this.current;
      this.isAnimating = false;
    }

    refreshAvailability() {
      if (!this.canSmooth) this.stop();
      document.documentElement.classList.toggle('aurara-smooth-scroll', this.canSmooth);
    }

    destroy() {
      this.stop();
      window.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('keydown', this.onKeyDown);
      this.motionQuery.removeEventListener('change', this.onMotionChange);
      document.documentElement.classList.remove('aurara-smooth-scroll');
    }
  }

  let controller;

  const settingsFromPage = () => {
    // A section can explicitly own the page-wide Lenis-style scrolling setting.
    // Home Belief is kept as a compatible marker for themes already using it.
    const configuredRoot = document.querySelector('[data-aurara-smooth-scroll][data-smooth-scroll-enabled], [data-aurara-belief][data-smooth-scroll-enabled]');
    if (configuredRoot) {
      if (configuredRoot.dataset.smoothScrollEnabled !== 'true') return null;
      return {
        enabled: true,
        lerp: Number(configuredRoot.dataset.smoothScrollLerp) || .09,
      };
    }

    // Cook Story is also a source-homepage section. Keep the original Lenis-like
    // scrolling available when merchants use it without the preceding Belief section.
    if (document.querySelector('[data-aurara-cook-story]')) {
      return { enabled: true, lerp: .09 };
    }

    // The original React Layout enables Lenis on every route. A Cooker page
    // can start with a model, comparison, feeding-comparison, or FAQ section,
    // so keep the same scroll feel without requiring a homepage-only section.
    if (document.querySelector('[data-aurara-cooker-model-select], [data-aurara-cooker-model-compare], [data-aurara-cooker-feeding-compare], [data-aurara-cooker-faq], [data-aurara-cooker-cross-sell], [data-aurara-recipes-hero], [data-aurara-recipes-library], [data-aurara-recipes-glossary], [data-aurara-recipes-meal-guide], [data-aurara-recipes-social-cta], [data-aurara-story-hero], [data-aurara-story-chapters], [data-aurara-story-why-fresh], [data-aurara-story-pack], [data-aurara-story-closing], [data-aurara-support-hero], [data-aurara-support-quickstart], [data-aurara-support-faq], [data-aurara-support-policies], [data-aurara-support-contact], [data-aurara-support-micro-cta], [data-aurara-product-hero], [data-aurara-product-whole-ingredients], [data-aurara-product-gentle-cook], [data-aurara-product-shredded], [data-aurara-product-warm-meal], [data-aurara-product-cleanup], [data-aurara-product-selling-points], [data-aurara-product-specs], [data-aurara-product-comparison], [data-aurara-product-faq], [data-aurara-recipe-article-hero], [data-aurara-recipe-details]')) {
      return { enabled: true, lerp: .09 };
    }

    // The React Layout enables Lenis on every route, including pages that
    // only contain standard Shopify sections. Keep the same global behavior.
    return { enabled: true, lerp: .09 };
  };

  const refresh = () => {
    const settings = settingsFromPage();
    if (!settings) {
      if (controller) {
        controller.destroy();
        controller = null;
      }
      return;
    }

    if (!controller) controller = new AuraraSmoothScrollController(settings);
    else controller.update(settings);
  };

  window.AuraraSmoothScroll = { refresh, destroy: () => controller && controller.destroy() };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  document.addEventListener('shopify:section:load', () => window.setTimeout(refresh, 0));
  document.addEventListener('shopify:section:unload', () => window.setTimeout(refresh, 0));
})();
